import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Trash2, ShoppingBag, MapPin, Check } from "lucide-react";
import { toast } from "sonner";

export default function Cart() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [checking, setChecking] = useState(false);
  const [postal, setPostal] = useState("");
  const [relayPoints, setRelayPoints] = useState([]);
  const [selectedRelay, setSelectedRelay] = useState(null);
  const [searching, setSearching] = useState(false);
  const [shipInfo, setShipInfo] = useState(null);

  useEffect(() => {
    api.get("/shipping/fee").then(r => setShipInfo(r.data)).catch(() => {});
  }, []);

  const total = items.reduce((s, i) => s + (i.price || 0), 0);
  const sizeRank = { small: 0, medium: 1, large: 2 };
  const maxSize = items.reduce((m, i) => (sizeRank[i.package_size || "small"] > sizeRank[m] ? (i.package_size || "small") : m), "small");
  const rawFee = shipInfo ? (shipInfo.fees?.[maxSize] || 0) : null;
  const feeEnabled = !!shipInfo?.enabled && rawFee > 0;
  const freeFrom = shipInfo?.free_from;
  const isFree = feeEnabled && freeFrom != null && total >= freeFrom;
  const effectiveFee = feeEnabled && !isFree ? rawFee : 0;
  const buyerFeePct = shipInfo?.buyer_fee_pct || 0;
  const buyerFee = Math.round(total * buyerFeePct * 100) / 100;

  useEffect(() => { if (!loading && !user) navigate("/login"); }, [user, loading, navigate]);

  useEffect(() => {
    if (user) api.get("/cart").then(r => setItems(r.data)).catch(()=>{});
  }, [user]);

  const remove = async (id) => {
    try { await api.delete(`/cart/${id}`); setItems(items.filter(i => i.product_id !== id)); }
    catch { toast.error("Erreur"); }
  };

  const searchRelays = async () => {
    if (postal.length < 5) { toast.error("Code postal incomplet"); return; }
    setSearching(true);
    try {
      const { data } = await api.get(`/shipping/relay-points?postal_code=${postal}`);
      setRelayPoints(data);
      if (data.length === 0) toast.error("Aucun relais trouvé");
    } catch { toast.error("Erreur"); }
    finally { setSearching(false); }
  };

  const checkout = async () => {
    if (items.length === 0) return;
    if (!selectedRelay) { toast.error("Choisis un point relais"); return; }
    setChecking(true);
    try {
      const { data } = await api.post("/checkout/session", {
        product_ids: items.map(i => i.product_id),
        origin_url: window.location.origin,
        relay_point_id: selectedRelay.id,
      });
      window.location.href = data.url;
    } catch {
      toast.error("Erreur paiement");
      setChecking(false);
    }
  };

  if (!user) return <div className="p-20 text-center">Chargement…</div>;

  return (
    <div className="max-w-5xl mx-auto px-4 lg:px-8 py-6 lg:py-10 fade-in has-bottom-nav" data-testid="cart-page">
      <p className="label mb-2">Panier</p>
      <h1 className="text-3xl lg:text-5xl font-black tracking-tighter mb-8">Ta sélection.</h1>

      {items.length === 0 ? (
        <div className="text-center py-16 border-2 border-dashed border-black">
          <ShoppingBag size={32} strokeWidth={2} className="mx-auto mb-4"/>
          <p className="text-2xl font-black mb-2">Panier vide 🛒</p>
          <p className="text-sm text-[color:var(--color-text-muted)] mb-6">Va dénicher des pépites.</p>
          <Link to="/catalogue" className="btn btn-primary">Explorer</Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-3">
            {items.map(p => (
              <div key={p.product_id} className="flex gap-3 p-3 border-2 border-black bg-white" data-testid={`cart-item-${p.product_id}`}>
                <Link to={`/produit/${p.product_id}`} className="w-24 h-24 shrink-0 overflow-hidden bg-[color:var(--color-surface)]">
                  {p.images?.[0] && <img src={fileUrl(p.images[0])} className="w-full h-full object-cover" alt=""/>}
                </Link>
                <div className="flex-1 min-w-0">
                  <p className="label">{p.brand}</p>
                  <Link to={`/produit/${p.product_id}`} className="font-bold block truncate">{p.title}</Link>
                  <p className="text-xs text-[color:var(--color-text-muted)]">{p.size && `Taille ${p.size}`}</p>
                  <p className="font-black mt-1" style={{color:"var(--color-coral)"}}>{p.price?.toFixed(0)}€</p>
                </div>
                <button onClick={()=>remove(p.product_id)} className="w-9 h-9 border-2 border-black flex items-center justify-center self-start hover:bg-[color:var(--color-coral)] hover:text-white hover:border-[color:var(--color-coral)]" data-testid={`remove-cart-${p.product_id}`}>
                  <Trash2 size={14}/>
                </button>
              </div>
            ))}

            {/* Mondial Relay selector */}
            <div className="p-4 border-2 border-black bg-white mt-6" data-testid="relay-selector">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 bg-[color:var(--color-coral)] text-white flex items-center justify-center"><MapPin size={16} strokeWidth={2.5}/></div>
                <div>
                  <p className="label font-bold">Livraison Mondial Relay</p>
                  <p className="text-xs text-[color:var(--color-text-muted)]">Choisis ton point relais</p>
                </div>
              </div>
              <div className="flex gap-2 mb-3">
                <input className="field flex-1" placeholder="Code postal (ex: 75001)" value={postal} onChange={e=>setPostal(e.target.value)} maxLength={5} data-testid="postal-input"/>
                <button onClick={searchRelays} disabled={searching} className="btn btn-primary" data-testid="search-relay-btn">{searching ? "..." : "Chercher"}</button>
              </div>
              {relayPoints.length > 0 && (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {relayPoints.map(r => (
                    <button key={r.id} onClick={()=>setSelectedRelay(r)}
                      className={`w-full text-left p-3 border-2 ${selectedRelay?.id === r.id ? 'border-black bg-[color:var(--color-mint)]' : 'border-[color:var(--color-border-soft)] bg-white hover:border-black'}`}
                      data-testid={`relay-${r.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="min-w-0">
                          <p className="font-bold text-sm">{r.name}</p>
                          <p className="text-xs text-[color:var(--color-text-muted)]">{r.address} · {r.postal_code} {r.city}</p>
                          <p className="text-xs mt-1">{r.hours}</p>
                        </div>
                        {selectedRelay?.id === r.id && <Check size={18} strokeWidth={2.5}/>}
                      </div>
                    </button>
                  ))}
                </div>
              )}
              {selectedRelay && (
                <div className="mt-3 p-2 bg-[color:var(--color-surface)] text-xs">
                  <span className="font-bold">✓ Sélectionné :</span> {selectedRelay.name} — {selectedRelay.city}
                </div>
              )}
            </div>
          </div>

          <aside className="lg:col-span-1">
            <div className="p-5 border-2 border-black bg-white sticky top-24">
              <p className="label font-bold mb-4">Récap</p>
              <div className="flex justify-between mb-2 text-sm">
                <span>Sous-total ({items.length})</span>
                <span className="font-bold">{total.toFixed(2)}€</span>
              </div>
              <div className="flex justify-between mb-2 text-sm">
                <span>Livraison Mondial Relay</span>
                <span className="font-bold" data-testid="shipping-fee">
                  {rawFee == null ? "…" : !feeEnabled ? <span className="text-[color:var(--color-text-muted)]">À régler en relais</span> : isFree ? <span className="text-[color:var(--color-mint-dark)]">OFFERTE 💚</span> : `${rawFee.toFixed(2)}€`}
                </span>
              </div>
              {buyerFeePct > 0 && (
                <div className="flex justify-between mb-2 text-sm">
                  <span>Protection acheteur ({Math.round(buyerFeePct * 100)}%)</span>
                  <span className="font-bold" data-testid="buyer-protection-fee">{buyerFee.toFixed(2)}€</span>
                </div>
              )}
              {feeEnabled && !isFree && (
                <div className="p-2 bg-[color:var(--color-yellow)] border border-black text-xs mb-2" data-testid="free-shipping-nudge">
                  🚚 Plus que <span className="font-black">{(freeFrom - total).toFixed(2)}€</span> d'achat pour la <span className="font-black">livraison offerte</span> !
                </div>
              )}
              <div className="hairline-b my-3"/>
              <div className="flex justify-between text-2xl font-black mb-4">
                <span>Total</span>
                <span style={{color:"var(--color-coral)"}} data-testid="cart-total">{(total + effectiveFee + buyerFee).toFixed(2)}€</span>
              </div>
              <div className="p-2 bg-[color:var(--color-mint)] text-xs mb-4 border border-black">
                <span className="font-bold">🛡 Paiement séquestré</span> — Les fonds sont libérés au vendeur uniquement après ta confirmation de réception.
              </div>
              <button className="btn btn-primary w-full" onClick={checkout} disabled={checking || !selectedRelay} data-testid="checkout-btn">
                {checking ? "Redirection…" : selectedRelay ? "Payer →" : "Choisis un point relais"}
              </button>
              <p className="text-xs mt-3 text-[color:var(--color-text-muted)]">Paiement sécurisé Stripe · Le vendeur reçoit 100% du prix de ses articles. La protection acheteur couvre le paiement séquestré et le support en cas de litige.</p>
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
